import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuButton from './MenuButton';
import LogoImg from '@/assets/Logo.svg';
import SearchIcon from '@/assets/search.svg?react';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import routes from '@/lib/routes';
import {
  isRouteAllowed,
  MenuOption,
  NavigationHeight,
} from './NavigationHelpers';
import { useAuthContext } from '@/contexts/AuthContext';
import { useAppLogout } from '@/hooks/useAppLogout';
import { useState } from 'react';
import GlobalSearchModal from './GlobalSearchModal';
import FeedbackModal from '@/components/FeedbackWidget/FeedbackModal';
import MySharedLinksModal from '@/components/MySharedLinksModal/MySharedLinksModal';
import useHasUserPermissions from '@/hooks/useHasUserPermissions';
import { SHARE_BASKET_PERMISSIONS } from '@/constants/Permissions';
import useGAPageName from '@/hooks/useGAPageName';
import { ga } from '@/lib/analytics';

const Navigation = () => {
  const navigate = useNavigate();
  const { user, isPending } = useAuthContext();
  const firstLetter = user?.name?.[0].toUpperCase();
  const handleLogout = useAppLogout();
  const isLoggedIn = user && !isPending;
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isSharedLinksOpen, setIsSharedLinksOpen] = useState(false);
  // A user who cannot create links has none to manage, so the entry point is
  // hidden rather than shown disabled.
  const canShare = useHasUserPermissions(SHARE_BASKET_PERMISSIONS);
  const gaPage = useGAPageName();

  const handleOpenGlobalSearch = () => {
    ga.trackMouseClick({
      component: 'global_search_trigger',
      origin_page: gaPage,
    });
    setIsSearchOpen(true);
  };

  return isLoggedIn ? (
    <AppBar
      position="static"
      sx={{ height: NavigationHeight, zIndex: 1 }}
      component="nav"
    >
      <StyledToolbar>
        <Logo
          src={LogoImg}
          alt="RubyPlay logo"
          onClick={() => navigate('/')}
        />
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'center',
          }}
          role="navigation"
          aria-label="Main navigation"
        >
          {routes.map((route) => {
            let options: MenuOption[] = [];
            if (route.routes) {
              options = route.routes
                .filter((subroute) => isRouteAllowed(user, subroute))
                .map((subroute) => ({
                  label: subroute.menuLabel,
                  path: subroute.path,
                  ariaLabel: subroute.ariaLabel,
                })) as MenuOption[];
            }

            if (
              !route.ariaLabel ||
              !isRouteAllowed(user, route) ||
              (route.routes?.length && !options.length)
            )
              return null;

            return (
              <MenuButton
                key={route.path}
                to={route.path}
                aria-label={route.ariaLabel!}
                options={options}
              >
                {route.menuLabel}
              </MenuButton>
            );
          })}
        </Box>
        <IconButton
          onClick={handleOpenGlobalSearch}
          aria-label="Open global search"
          sx={{ marginRight: 2 }}
        >
          <SearchIcon />
        </IconButton>
        <div>
          <MenuButton
            dense
            options={[
              {
                label: (
                  <UserInfo>
                    <UserWelcome>
                      <FormattedMessage
                        id="welcome"
                        defaultMessage="Welcome"
                      />
                    </UserWelcome>
                    {user?.email ? (
                      <UserEmail>{user.email}</UserEmail>
                    ) : null}
                  </UserInfo>
                ),
                onClick: () => {},
                ariaLabel: 'User information',
                key: 'user-info',
              },
              {
                label: 'User Guide',
                onClick: () =>
                  window.open(
                    'https://docs.rubyplay.com/content/partner-portal-user-guide/partner-portal-user-guide',
                    '_blank',
                  ),
                ariaLabel: 'User Guide',
                key: 'user-guide',
              },
              {
                label: (
                  <FormattedMessage
                    id="feedbackWidget.floatingButton"
                    defaultMessage="Share feedback"
                  />
                ),
                onClick: () => setIsFeedbackOpen(true),
                ariaLabel: 'Share feedback',
                key: 'share-feedback',
              },
              ...(canShare
                ? [
                    {
                      label: (
                        <FormattedMessage
                          id="mySharedLinks"
                          defaultMessage="My shared links"
                        />
                      ),
                      onClick: () => setIsSharedLinksOpen(true),
                      ariaLabel: 'My shared links',
                      key: 'my-shared-links',
                    },
                  ]
                : []),
              {
                label: 'Logout',
                onClick: () => handleLogout(),
                ariaLabel: 'Logout',
                key: 'logout',
              },
            ]}
            disableMatch
            aria-label="User menu"
          >
            <Avatar aria-label={`User avatar for ${user?.name}`}>
              {firstLetter}
            </Avatar>
          </MenuButton>
        </div>
      </StyledToolbar>
      <GlobalSearchModal
        open={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
      <FeedbackModal
        open={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
      />
      <MySharedLinksModal
        open={isSharedLinksOpen}
        onClose={() => setIsSharedLinksOpen(false)}
      />
    </AppBar>
  ) : null;
};

const Logo = styled('img')`
  width: 189px;
  height: 53px;
  cursor: pointer;
`;

const StyledToolbar = styled(Toolbar)`
  height: 100%;
  padding: 0 40px !important;
`;

const Avatar = styled('div')(({ theme }) => ({
  position: 'relative',
  width: 32,
  height: 32,
  background: theme.palette.primary500,
  color: theme.palette.textPrimaryWhite,
  borderRadius: '50%',
  fontWeight: 600,
  fontSize: 16,
  lineHeight: '32px',
  flexShrink: 0,

  '&:after': {
    content: '""',
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: theme.palette.green500,
    position: 'absolute',
    bottom: 0,
    right: 0,
    border: `1.5px solid ${theme.palette.textPrimaryWhite}`,
  },
}));

const UserInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  minWidth: 0,
  padding: theme.spacing(1, 0),
}));

const UserWelcome = styled('div')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.textPrimaryBlack,
}));

const UserEmail = styled('div')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 400,
  color: theme.palette.textSecondaryDarkGrey,
  wordBreak: 'break-all',
}));

export default Navigation;
